import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, StyleSheet } from 'react-native';
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import { ShouldStartLoadRequest } from 'react-native-webview/lib/WebViewTypes';

import { StackActions, useLinkTo } from '@react-navigation/native';

import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import Webview from '@/components/Webview';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { DEPOSIT_STATUS_DEEP_LINK_URL, DEPOSIT_STATUS_UNIVERSAL_LINK_URL } from '@/feature/deposit/const';
import { setDepositAmount, useDepositStatusStore } from '@/feature/deposit/hooks/use-deposit-status-store';
import { kycVerifiedSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { KycStackScreenProps } from '@/navigation/kyc/types';
import { navigateToKycSelector, popToHome } from '@/navigation/navigation';
import { save } from '@/utils/async-storage';
import { isWeb } from '@/utils/constants-platform-specific';
import { logger } from '@/utils/logging';

import { lightbox, widget } from '../../components/Trustly/TrustlyLightboxWidget';
import { useKycClose } from '../../hooks/use-kyc-close';
import { TrustlyEstablishData, TrustlyMessage, TrustlyStatus } from '../../types';
import { isTrustlyAppToAppReturnUrl, shouldOpenInAppBrowser } from '../../utils/oauth-utils';
import { useEstablishData } from './hooks/use-establish-data';
import { useTrustlyConfigurations } from './hooks/use-trustly-configurations';
import { TRUSTLY_KYC_ONLY_DEEPLINK_URL, TRUSTLY_KYC_ONLY_UNIVERSAL_LINK_URL } from './utils/const';

type ScreenProps = KycStackScreenProps<'TrustlyWebview'>;

const LoadingIndicator = () => (
    <Box alignItems="center" justifyContent="center" height="100%">
        <LoadingSpinner />
    </Box>
);

export const TrustlyWebview = ({ navigation, route }: ScreenProps) => {
    const { transactionId, payment_type_id, isTrustlyIdOnly = false } = route.params ?? {};
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const amount = isTrustlyIdOnly ? '0.00' : Number(selectedAmount).toFixed(2);
    const { t } = useTranslation('account');

    const { data: isKycVerified } = useAuthUserConfig({ select: kycVerifiedSelector });
    const hasSocialWallet = useHasSocialWallet();
    const rootNavigation = useRootNavigation();
    const linkTo = useLinkTo();
    const { onClose: logoutToLogin } = useKycClose(true);

    const trustlyWebviewRef = useRef<Webview>(null);
    const transactionIdRef = useRef(null);
    const [lightboxComponent, setLightboxComponent] = useState<React.ReactNode>(null);
    const [step, setStep] = useState('');

    const isRefreshSplitToken = Boolean(transactionId && payment_type_id);

    const { accessId: trustlyAccessId } = useTrustlyConfigurations();
    const establishData = useEstablishData({
        amount,
        isRefreshSplitToken,
        paymentTypeId: payment_type_id,
        transactionId,
        isTrustlyIdOnly,
    });

    const openDeepLink = (deepLinkUrl: string) => linkTo(deepLinkUrl.replace('betrpickem://', '/'));

    const handleShouldStartLoadWithRequest = (request: Pick<ShouldStartLoadRequest, 'url'>): boolean => {
        if (shouldOpenInAppBrowser(request.url)) {
            Linking.openURL(request.url).catch(error => {
                logger.error('Trustly', 'Failed to open OAuth URL in browser', error);
            });
            return false;
        }
        if (request.url.startsWith(TRUSTLY_KYC_ONLY_UNIVERSAL_LINK_URL)) {
            const url = new URL(request.url);
            const params = new URLSearchParams(url.search);
            const currentStatus = params.get('currentStatus') || '';

            openDeepLink(`${TRUSTLY_KYC_ONLY_DEEPLINK_URL}?currentStatus=${currentStatus}`);
            return false;
        }
        if (request.url.startsWith(DEPOSIT_STATUS_UNIVERSAL_LINK_URL)) {
            const url = new URL(request.url);
            const params = new URLSearchParams(url.search);

            const amountSelected = params.get('selectedAmount') || '';
            const paymentProvider = params.get('paymentProvider') || '';
            const currentStatus = params.get('currentStatus') || '';

            if (currentStatus === TrustlyStatus.TRANSACTION || currentStatus === TrustlyStatus.REFRESH) {
                openDeepLink(
                    `${DEPOSIT_STATUS_DEEP_LINK_URL}?selectedAmount=${amountSelected}&paymentProvider=${paymentProvider}&currentStatus=${currentStatus}`
                );
            } else {
                // goes back to DepositStack if it's below (deposit flow)
                // otherwise opens it on top of KycStack (e.g. coin package during KYC)
                navigation.navigate(
                    'DepositStack',
                    { screen: 'FailedDepositModal', params: { paymentProvider } },
                    { pop: true }
                );
            }
            return false;
        }
        return true;
    };

    const getMessageValue = (str: string) => {
        try {
            return JSON.parse(str);
        } catch {
            return str;
        }
    };

    const handleTrustlyMessages = (msg: TrustlyMessage) => {
        if (isWeb && msg.nativeEvent.url !== window.location.origin) {
            return;
        }
        let message = msg.nativeEvent.data;
        if (typeof message !== 'string') {
            return;
        }

        const [command, payloadRaw] = message.split('|');
        const payload = getMessageValue(payloadRaw);

        // Go back to KYC Selector if improper payload is received from trustly.
        if (!payload) {
            return navigateToKycSelector(navigation);
        }

        if (payload && transactionIdRef.current !== payload.transactionId) {
            transactionIdRef.current = payload.transactionId;
            if (transactionIdRef.current) {
                save('trustlyTransactionId', transactionIdRef.current);
            }
        }

        switch (command) {
            case 'PayWithMyBank.createTransaction':
                if (!isRefreshSplitToken) {
                    (establishData as TrustlyEstablishData).paymentProviderId = payload;
                    buildLightBoxScreen();
                }
                break;
            case 'close':
                if (isKycVerified && typeof payload === 'string' && payload.includes(TrustlyStatus.CANCEL)) {
                    setDepositAmount(amount);
                    // DepositStack is already rendered below, so we pop to it
                    // `pop` makes the nested navigator go back to that screen instead of pushing a second copy
                    return navigation.dispatch(
                        StackActions.popTo('DepositStack', {
                            screen: hasSocialWallet ? 'CoinPackageScreen' : 'DepositScreen',
                            pop: true,
                        })
                    );
                }
                if (isTrustlyIdOnly) {
                    // with the native stack, "goBack" fires instantly
                    // so the web view is unmounted before getting to the "onShouldStartLoadWithRequest" handler
                    // in this specific case, we let the "onShouldStartLoadWithRequest" handler handle the navigation back to the KYC selector
                    if (typeof payload === 'string' && payload.startsWith(TRUSTLY_KYC_ONLY_UNIVERSAL_LINK_URL)) {
                        return;
                    }
                    return navigation.goBack();
                }
                break;
            case 'event':
                // web only: the lightbox cancels Trustly's returnUrl/cancelUrl redirect and hands us the url instead
                if (isWeb && payload.type === 'new_location' && typeof payload.data === 'string') {
                    handleShouldStartLoadWithRequest({ url: payload.data });
                }
                break;
        }
    };

    const handleOAuthResult = () => {
        trustlyWebviewRef.current?.injectJavaScript(
            'if (window.Trustly && window.Trustly.proceedToChooseAccount) { window.Trustly.proceedToChooseAccount(); } true;'
        );
    };

    useEffect(() => {
        if (step !== 'lightbox') {
            return;
        }
        const subscription = Linking.addEventListener('url', ({ url }) => {
            if (isTrustlyAppToAppReturnUrl(url)) {
                handleOAuthResult();
            }
        });
        return () => subscription.remove();
    }, [step]);

    const handleClose = () => {
        if (!isKycVerified) {
            return logoutToLogin?.();
        }

        if (isTrustlyIdOnly) {
            return popToHome(rootNavigation, 'account');
        }

        navigation.goBack();
    };

    const buildWidgetScreen = () => (
        <SafeAreaProvider style={styles.backgroundStyle}>
            <SafeAreaView style={styles.backgroundStyle}>
                <ScreenNavBar title={t('link_bank_account')} closeIconMode="close" onClose={handleClose} />
                <Webview
                    ref={trustlyWebviewRef}
                    source={{
                        html: widget(trustlyAccessId, establishData as TrustlyEstablishData),
                    }}
                    renderLoading={LoadingIndicator}
                    onMessage={handleTrustlyMessages}
                    javaScriptEnabled
                    startInLoadingState
                    style={styles.widget}
                />
            </SafeAreaView>
        </SafeAreaProvider>
    );

    const buildLightBoxScreen = async () => {
        const html: string = (await lightbox(trustlyAccessId, establishData)) ?? '';
        setLightboxComponent(
            <SafeAreaProvider style={styles.backgroundStyle}>
                <SafeAreaView style={styles.backgroundStyle}>
                    <Webview
                        ref={trustlyWebviewRef}
                        source={{ html }}
                        renderLoading={LoadingIndicator}
                        onMessage={handleTrustlyMessages}
                        javaScriptEnabled
                        startInLoadingState
                        style={styles.widget}
                        onShouldStartLoadWithRequest={handleShouldStartLoadWithRequest}
                    />
                </SafeAreaView>
            </SafeAreaProvider>
        );
        setStep('lightbox');
    };

    const renderScreen = () => {
        if (step === 'lightbox') {
            return lightboxComponent;
        }

        if (isRefreshSplitToken) {
            buildLightBoxScreen();
        }

        return buildWidgetScreen();
    };

    return renderScreen();
};

const styles = StyleSheet.create({
    backgroundStyle: {
        backgroundColor: ds.colorValue.background.neutral.base,
        flex: 1,
    },
    widget: {
        width: '100%',
        height: '100%',
    },
});
